import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

import { PROFILE } from 'constants/profileKeys';

// ============================================================================
// Inicio por perfil
// ============================================================================
//
// Una sola tabla declarativa que dice, para cada perfil, A DÓNDE entra al
// iniciar sesión y QUÉ tablero ve ahí. Agregar un perfil es agregar un
// renglón; no hay que tocar el login ni el componente del tablero.
//
// Reemplaza dos cosas que estaban quemadas y envejecían mal:
//
//   1. En Login.tsx: `if (keyProfile === PROFILE.DASHBOARD) navigate('/panel')`.
//      Cada perfil nuevo pedía otro if.
//   2. En el tablero: un solo componente que decidía qué paneles mostrar a
//      partir de los permisos. Eso ya nos falló: el coordinador de
//      procesamiento acabó viendo el avance de AUDITORÍA porque compartía un
//      permiso con el administrador.
//
// Mismo patrón que routes/viewRegistry.ts, que ya se usa para las vistas del
// menú: key estable -> componente, resuelto en runtime. Las keys de perfil
// salen de constants/profileKeys.ts (son aleatorias, db/113).
// ============================================================================

export interface HomeDefinition {
  /** A dónde se manda al usuario después de iniciar sesión. */
  path: string;
  /** Tablero que se pinta en `/`. Sin componente, el perfil no entra a `/`. */
  dashboard?: LazyExoticComponent<ComponentType<any>>;
}

const HOME_BY_PROFILE: Record<string, HomeDefinition> = {
  // Administrador: ve las dos cadenas, auditoría y procesamiento.
  [PROFILE.ADMIN]: {
    path: '/',
    dashboard: lazy(() => import('views/admin/Dashboard')),
  },

  // Administrador de ASF (db/106): tablero PROPIO, no el del Administrador.
  //
  // Arranca como copia fiel del de admin, pero separado a proposito (pedido
  // del usuario 2026-10-07): a este perfil se le van a quitar paneles y al del
  // Administrador no. Compartir el archivo obligaria a condicionar panel por
  // panel, que es como se rompio antes el tablero del coordinador de
  // procesamiento.
  [PROFILE.ADMIN_ASF]: {
    path: '/',
    dashboard: lazy(() => import('views/admin-asf/Dashboard')),
  },

  // Directora y Subdirectores de área (db/120): el MISMO tablero que el
  // Administrador de ASF (pedido del usuario 2026-10-08). Sus números salen
  // acotados a los estados de su Estructura porque el API los filtra
  // (WorkScope::visible + WorkAccess), no porque el tablero sea otro.
  [PROFILE.AREA_DIRECTOR]: {
    path: '/',
    dashboard: lazy(() => import('views/admin-asf/Dashboard')),
  },
  [PROFILE.AREA_SUBDIRECTOR]: {
    path: '/',
    dashboard: lazy(() => import('views/admin-asf/Dashboard')),
  },
  // Jefe de Departamento y Coordinador de Auditores (db/179): arrancan como
  // copia del Subdirector, tablero incluido (pedido del usuario 2026-10-19).
  [PROFILE.DEPT_HEAD]: {
    path: '/',
    dashboard: lazy(() => import('views/admin-asf/Dashboard')),
  },
  [PROFILE.AUDITOR_COORDINATOR]: {
    path: '/',
    dashboard: lazy(() => import('views/admin-asf/Dashboard')),
  },
  // Titular y Secretario Técnico de la AEM (db/193, pedido 2026-10-21): con lo
  // del Admin ASF, tablero incluido. Sus números salen acotados a Municipales.
  [PROFILE.AEM_AUDITOR]: {
    path: '/',
    dashboard: lazy(() => import('views/admin-asf/Dashboard')),
  },
  [PROFILE.AEM_SECRETARY]: {
    path: '/',
    dashboard: lazy(() => import('views/admin-asf/Dashboard')),
  },

  // (El Coordinador de auditoría ya no existe: db/121 lo dio de baja y su
  // papel lo tiene el Subdirector de área.)

  // Coordinador de procesamiento: su cadena es vuelo -> NAS -> proceso.
  [PROFILE.PROCESSING]: {
    path: '/',
    dashboard: lazy(() => import('views/processing/Dashboard')),
  },

  // Auditor: solo lo suyo.
  [PROFILE.AUDITOR]: {
    path: '/',
    dashboard: lazy(() => import('views/auditor/Dashboard')),
  },

  // Dronero: solo sus vuelos.
  [PROFILE.PILOT]: {
    path: '/',
    dashboard: lazy(() => import('views/pilot/Dashboard')),
  },

  // Legal: convenios de los pilotos y vigencia de sus licencias AFAC.
  //
  // Tiene su PROPIO tablero, como todos los perfiles. La primera version lo
  // mandaba directo al padron sin definir tablero, y eso rompia la ruta "/":
  // DynamicRoute resuelve esa ruta buscando el item de menu con url "/", que
  // pide el permiso dashboard.view, y al no tenerlo el perfil no encontraba
  // match y le salia "Ruta no asociada a vista". El permiso se otorga en
  // db/082; no protege ningun endpoint, solo habilita esa ruta.
  [PROFILE.LEGAL]: {
    path: '/',
    dashboard: lazy(() => import('views/legal/Dashboard')),
  },

  // Perfil de pantalla: entra directo al panel GeoAudit a pantalla completa,
  // no al backoffice. Por eso no define tablero.
  [PROFILE.DASHBOARD]: {
    path: '/panel',
  },
};

/** Tablero de respaldo: perfil desconocido o sin tablero propio. */
const DEFAULT_DASHBOARD = lazy(() => import('views/dashboard/DefaultDashboard'));

/** A dónde mandar al usuario al iniciar sesión. */
export function homePathFor(keyProfile?: string | null): string {
  return (keyProfile && HOME_BY_PROFILE[keyProfile]?.path) || '/';
}

/** Qué tablero pintar en `/` para este perfil. */
export function dashboardFor(keyProfile?: string | null): LazyExoticComponent<ComponentType<any>> {
  return (keyProfile && HOME_BY_PROFILE[keyProfile]?.dashboard) || DEFAULT_DASHBOARD;
}

export default HOME_BY_PROFILE;
